import {
  createContext,
  useContext,
  useState,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
} from 'react';
import { IWidgetParams, IStyleOverride } from '../types/WidgetParams';
import { widgetParams } from '../mock/widgetParams';
import {
  IUTConfiguration,
  defaultUTConfiguration,
} from '../types/UTConfiguration';
import { getConfiguration, amIAuthorized } from '../services/Configuration';
import { Typography } from '@mui/material';
import { mergeDeep } from '../utils/DeepMerge';
import { JSONParse } from '../utils/JSONParse';

const CUSTOM_EVENT_TRIGGER_NAME = 'storable-webapp-ut-event';

type EventTriggerFn = (
  eventName: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventData: Record<string, any>,
) => void;

type EventDispatcherFn = (customEvent: CustomEvent) => void;

interface IConfigContext {
  widgetParams: IWidgetParams;
  configuration: IUTConfiguration;
  refreshConfig: () => void;
  triggerEvent: EventTriggerFn;
  hasAuthError: boolean;
}

const defaultConfigContext: IConfigContext = {
  widgetParams,
  configuration: defaultUTConfiguration,
  refreshConfig: () => {},
  triggerEvent: () => {},
  hasAuthError: false,
};

const ConfigContext = createContext(defaultConfigContext);

export const useConfig = () => useContext(ConfigContext);

const mergeStyleInfo = (config: IUTConfiguration, styleString: string) => {
  const styleInfo = JSONParse<IStyleOverride>(styleString);
  const updatedConfig = { ...config };
  updatedConfig.themification = mergeDeep(
    updatedConfig.themification as unknown as Record<string, unknown>,
    styleInfo as unknown as Record<string, unknown>,
  ) as IUTConfiguration['themification'];
  return updatedConfig;
};

const useConfigProvider = (
  widgetParamsInfo: IWidgetParams,
  onEvent: EventDispatcherFn,
): IConfigContext => {
  const [config, setConfig] = useState<IUTConfiguration>(
    defaultUTConfiguration,
  );
  const [hasAuthError, setAuthError] = useState(false);

  useEffect(() => {
    if (!widgetParamsInfo.accessKey) {
      setAuthError(true);
    }
    amIAuthorized(widgetParamsInfo.accessKey).catch(() => {
      setAuthError(true);
    });
  }, [widgetParamsInfo]);

  const fetchConfig = useCallback(() => {
    getConfiguration(widgetParamsInfo)
      .then((resp) => {
        setConfig(
          mergeStyleInfo(
            { ...defaultUTConfiguration, ...resp.configuration },
            widgetParamsInfo.style || '',
          ),
        );
      })
      .catch((err) => {
        console.error('Error fetching configuration', err);
        setConfig(defaultUTConfiguration);
      });
  }, [widgetParamsInfo]);

  useEffect(fetchConfig, []);

  const triggerEvent: EventTriggerFn = (eventName, eventData) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).storableUSWidget?.onEvent?.({
      event: eventName,
      data: eventData,
    });
    onEvent(
      new CustomEvent(CUSTOM_EVENT_TRIGGER_NAME, {
        detail: { eventName, data: eventData },
        bubbles: true,
        composed: true,
      }),
    );
  };

  return {
    widgetParams: { ...widgetParams, ...widgetParamsInfo },
    configuration: config,
    refreshConfig: fetchConfig,
    triggerEvent,
    hasAuthError,
  };
};

export const ConfigProvider: FC<
  PropsWithChildren<Partial<IWidgetParams> & { onEvent: EventDispatcherFn }>
> = ({ children, onEvent, ...widgetParamsData }) => {
  let modifiedWidgetParams = { ...widgetParams, ...widgetParamsData };

  // If provider is SL then convert org and fac to uppercase
  if (widgetParams?.providerId?.toLowerCase() === 'sl') {
    modifiedWidgetParams = {
      ...modifiedWidgetParams,
      organizationId: modifiedWidgetParams?.organizationId?.toUpperCase() || '',
      facilityId: modifiedWidgetParams?.facilityId?.toUpperCase() || '',
    };
  }

  const providerValue = useConfigProvider(
    {
      ...modifiedWidgetParams,
    },
    onEvent,
  );
  if (providerValue.hasAuthError) {
    return (
      <Typography>You are not authorized to access this Web App</Typography>
    );
  }
  return (
    <ConfigContext.Provider value={providerValue}>
      {children}
    </ConfigContext.Provider>
  );
};
